import { Modal, Form, Input } from 'antd'
import { UnlockOutlined } from '@ant-design/icons'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import * as devicesApi from '../../api/devices.api'
import { useEffect } from 'react'
import { toast, handleMutationError } from '../../lib/notification'

interface Props {
  deviceId: string
  open: boolean
  onClose: () => void
}

export default function UnlockDeviceModal({ deviceId, open, onClose }: Props) {
  const [form] = Form.useForm()
  const queryClient = useQueryClient()

  const unlockMutation = useMutation({
    mutationFn: (values: { message?: string }) =>
      devicesApi.unlockDevice(deviceId, values),
    onSuccess: (data) => {
      toast.success(data.message)
      form.resetFields()
      onClose()
      queryClient.invalidateQueries({ queryKey: ['devices'] })
      queryClient.invalidateQueries({ queryKey: ['devices', deviceId] })
    },
    onError: (error: unknown) => {
      handleMutationError(error, 'Failed to unlock device')
    },
  })

  useEffect(() => {
    if (!open) {
      form.resetFields()
    }
  }, [open, form])

  const handleSubmit = () => {
    unlockForm.validateFields().then((values) => {
      unlockMutation.mutate(values)
    })
  }

  // Fix: use local alias for the form
  const unlockForm = form

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <UnlockOutlined style={{ color: '#10b981' }} />
          <span>Unlock Device</span>
        </div>
      }
      open={open}
      onOk={handleSubmit}
      onCancel={onClose}
      okText="Unlock Device"
      okButtonProps={{
        loading: unlockMutation.isPending,
        style: { background: '#10b981', borderColor: '#10b981' },
      }}
      width={600}
    >
      <div style={{ marginTop: 24 }}>
        <div
          style={{
            padding: 16,
            borderRadius: 12,
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            marginBottom: 24,
          }}
        >
          <p style={{ margin: 0, color: '#166534', fontSize: 13, fontWeight: 500 }}>
            This will unlock the device and remove all lock restrictions.
          </p>
        </div>
        <Form form={form} layout="vertical">
          <Form.Item
            name="message"
            label="Unlock Message (Optional)"
          >
            <Input.TextArea
              rows={3}
              placeholder="e.g., Payment received. Your device has been unlocked. Thank you!"
              style={{ borderRadius: 8 }}
            />
          </Form.Item>
        </Form>
      </div>
    </Modal>
  )
}
